// HandleCache.vue

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import packageApp from "../../../package.json";

// State management
const isLoading = ref(true);
const isLatestVersionAvailable = ref(false);
const appVersion = ref(packageApp.version);
const versionCheckInterval = ref(null);

// Enhanced cookie clearing with domain handling
const clearCookies = () => {
  const domainParts = window.location.hostname.split(".");
  const domains = [
    window.location.hostname,
    ...(domainParts.length > 2
      ? [`.${domainParts.slice(-2).join(".")}`] // Add root domain for subdomains
      : []),
  ];

  document.cookie.split(";").forEach((cookie) => {
    const [name] = cookie.trim().split("=");
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
    });
  });
};

// More reliable cache clearing
const clearCacheAndReload = async () => {
  try {
    // Clear service workers first
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }

    // Clear caches
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }

    // Clear cookies
    clearCookies();

    // Clear localStorage and sessionStorage (optional)
    localStorage.clear();
    sessionStorage.clear();

    // Store new version and reload
    localStorage.setItem("appVersion", appVersion.value);
    window.location.href =
      window.location.href.split("?")[0] + "?t=" + Date.now();
  } catch (error) {
    console.error("Cache clearing failed:", error);
    window.location.reload(true);
  }
};

// Robust version comparison
const checkVersionMismatch = (latest, current) => {
  const parseVersion = (v) => v.split(".").map(Number);
  const [latestParts, currentParts] = [
    parseVersion(latest),
    parseVersion(current),
  ];

  for (let i = 0; i < Math.max(latestParts.length, currentParts.length); i++) {
    const [l = 0, c = 0] = [latestParts[i], currentParts[i]];
    if (l > c) return true;
    if (l < c) return false;
  }
  return false;
};

// Version checking with retry logic
const checkVersion = async (attempt = 1) => {
  try {
    const response = await fetch(`/meta.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

    if (!response.ok) throw new Error("Network response not ok");

    const meta = await response.json();
    const latestVersion = meta.version;
    const storedVersion =
      localStorage.getItem("appVersion") || appVersion.value;

    const needsUpdate = checkVersionMismatch(latestVersion, storedVersion);

    if (needsUpdate) {
      console.log(
        `Version update required: ${storedVersion} → ${latestVersion}`
      );
      isLatestVersionAvailable.value = false;
      await clearCacheAndReload();
    } else {
      console.log(`Running latest version: ${latestVersion}`);
      localStorage.setItem("appVersion", latestVersion);
      isLatestVersionAvailable.value = true;
    }
  } catch (error) {
    console.error(`Version check failed (attempt ${attempt}):`, error);
    if (attempt < 3) {
      setTimeout(() => checkVersion(attempt + 1), 2000 * attempt);
      return;
    }
    // Fallback - allow app to run but warn user
    isLatestVersionAvailable.value = true;
  } finally {
    isLoading.value = false;
  }
};

// Periodic version checking
const startVersionChecker = () => {
  checkVersion();
  versionCheckInterval.value = setInterval(checkVersion, 15 * 60 * 1000); // Check every 15 mins
};

onMounted(() => {
  startVersionChecker();
});

onBeforeUnmount(() => {
  clearInterval(versionCheckInterval.value);
});

defineExpose({
  clearCacheAndReload,
  forceVersionCheck: checkVersion,
});
</script>

<template>
  <div>
    <slot
      :isLoading="isLoading"
      :isLatestVersionAvailable="isLatestVersionAvailable"
      :appVersion="appVersion"
      :clearCacheAndReload="clearCacheAndReload"
    />
  </div>
</template>

<!-- <script setup>
import { ref, onMounted } from "vue";
import packageApp from "../../../package.json";

const isLoading = ref(true);
const isLatestVersionAvailable = ref(false);
const appVersion = ref(packageApp.version);

// Function to clear cookies
const clearCookies = () => {
  console.log("Clearing cookies...");
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
    // Set the cookie expiration date to the past
    // Set the cookie expiration to a past date and specify path and domain
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;

    // Also try clearing for the root path in case it was set elsewhere
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  }
};

const clearCacheAndReload = () => {
  console.log('Clearing cache and hard reloading...');
  if (caches) {
    caches.keys().then((names) => {
      for (let name of names) caches.delete(name);
      console.log('clearing', names)
    });
  }

  // Clear cookies before reloading
  clearCookies();

  // Store the latest version in localStorage to avoid redundant reloads
  localStorage.setItem('appVersion', appVersion.value);
  window.location.reload(true);
};


const checkVersionMismatch = (latestVersion, currentVersion) => {
  const latestVersionArray = latestVersion.split(/\./g);
  const currentVersionArray = currentVersion.split(/\./g);
  while (latestVersionArray.length || currentVersionArray.length) {
    const a = Number(latestVersionArray.shift());
    const b = Number(currentVersionArray.shift());
    if (a === b) continue;
    return a > b || isNaN(b);
  }
  return false;
};

onMounted(() => {
  fetch(`/meta.json?${new Date().getTime()}`, { cache: "no-cache" })
    .then((response) => response.json())
    .then((meta) => {
      const latestVersion = meta.version;
      const currentVersion = appVersion.value;
      const storedVersion = localStorage.getItem("appVersion");
      const shouldForceRefresh = checkVersionMismatch(
        latestVersion,
        storedVersion || currentVersion
      );

      console.log(shouldForceRefresh);
      if (shouldForceRefresh) {
        console.log(
          `New version - ${latestVersion}. Available, need to force refresh`
        );
        isLoading.value = false;
        isLatestVersionAvailable.value = false;
      } else {
        console.log(
          `Already latest version - ${latestVersion}. No refresh required.`
        );
        // Store the current version in localStorage for future checks
        localStorage.setItem("appVersion", appVersion.value);
        isLoading.value = false;
        isLatestVersionAvailable.value = true;
      }
    });
});

defineExpose({
  clearCacheAndReload,
});
</script>

<template>
  <div>
    <slot
      :isLoading="isLoading"
      :isLatestVersionAvailable="isLatestVersionAvailable"
    />
  </div>
</template> -->
